import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { FeathericonComponent } from '../../shared/component/feathericon/feathericon.component';
import { AuthService } from '../auth.service';
import { ToastService } from '../../shared/services/toast.service';
import { buildAppRoutes } from '../../app.routes';

@Component({
  selector: 'app-change-password',
  imports: [FormsModule, ReactiveFormsModule, CommonModule, FeathericonComponent],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss'
})
export class ChangePasswordComponent implements OnInit, AfterViewInit {
  changePasswordForm!: FormGroup;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  ngAfterViewInit(): void {
    const modalEl = this.el.nativeElement.querySelector('#ChangePassword');
    if (modalEl) {
      modalEl.addEventListener('hidden.bs.modal', () => {
        this.changePasswordForm.reset();
      });
    }
  }
  togglePasswordVisibility(field: string) {
  if (field === 'current') {
    this.showCurrentPassword = !this.showCurrentPassword;
  } else if (field === 'new') {
    this.showNewPassword = !this.showNewPassword;
  } else if (field === 'confirm') {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
}
  constructor(
    private fb: FormBuilder,
    private el: ElementRef,
    private authService: AuthService,
    private toast: ToastService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.changePasswordForm = this.fb.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
            )
          ]
        ],
        confirmPassword: ['', Validators.required]
      },
      { validators: this.passwordMatchValidator }
    );
  }

  get f() {
    return this.changePasswordForm.controls;
  }

passwordMatchValidator(formGroup: AbstractControl): ValidationErrors | null {
  const currentPassword = formGroup.get('currentPassword')?.value;
  const newPassword = formGroup.get('newPassword')?.value;
  const confirmPassword = formGroup.get('confirmPassword')?.value;

  const errors: any = {};

  // ✅ 1. Check if new password matches confirm password
  if (newPassword && confirmPassword && newPassword !== confirmPassword) {
    errors['mismatch'] = true;
  }

  // ✅ 2. Check if new password is same as current
  if (currentPassword && newPassword && currentPassword === newPassword) {
    errors['sameAsCurrent'] = true;
  }

  return Object.keys(errors).length ? errors : null;
}

  onSubmit(): void {
    if (this.changePasswordForm.valid) {
      const loginId = localStorage.getItem('login_id');

      if (!loginId) {
        this.toast.showToast('Login ID not found. Please log in again.', 'colored', 5000, 'danger');
        return;
      }

      // ✅ Create FormData for API
      const formData = new FormData();
      formData.append('login_id', loginId);
      formData.append('current_password', this.f['currentPassword'].value);
      formData.append('new_password', this.f['newPassword'].value);

      console.log(formData);

      this.authService.changePassword(formData).subscribe({
        next: (res: any) => {
          if (res?.Status === 'Error' || res?.error === true) {
            this.toast.showToast(res?.Message || 'Failed to change password.', 'colored', 5000, 'danger');
            return;
          }

          this.changePasswordForm.reset();

          const modalEl = document.getElementById('ChangePassword');
          if (modalEl) {
            const modalInstance = (window as any).bootstrap?.Modal?.getInstance(modalEl);
            if (modalInstance) {
              modalInstance.hide();
            }
          }
          const backdrops = document.querySelectorAll('.modal-backdrop');
          backdrops.forEach(b => b.remove());
          document.body.classList.remove('modal-open');
          document.body.style.removeProperty('overflow');
          document.body.style.removeProperty('padding-right');

          Swal.fire({
            title: 'Password Changed',
            text: 'Password has been changed, and the page will now logout.',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3085d6',
            timer: 3000,
            timerProgressBar: true,
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then(() => {
            this.logOut();
          });
        },
        error: (err: any) => {
          console.error('❌ Error changing password:', err);
          this.toast.showToast(err?.error?.Message || 'Failed to change password. Please try again.', 'colored', 5000, 'danger');
        },
      });
    } else {
      this.changePasswordForm.markAllAsTouched();
    }
  }

  logOut(): void {
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('token');
    localStorage.removeItem('calculatedExpiry');
    sessionStorage.removeItem('calculatedExpiry');
    localStorage.removeItem('loginTimestamp');
    localStorage.removeItem('sidebarItems');
    localStorage.removeItem('sideMenuList');
    localStorage.removeItem('lastActiveUrl');
    localStorage.removeItem('redirect');

    this.router.resetConfig(buildAppRoutes());
    this.router.navigate(['/auth/login']);
  }

  // Trigger validation on blur or input
  onFieldBlur(field: string) {
    this.f[field].markAsTouched();
  }

  onFieldInput(field: string) {
    // Trigger re-validation while typing
    if (this.f[field].touched) {
      this.f[field].updateValueAndValidity({ onlySelf: true });
    }
  }
}
